import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DoCheck,
  effect,
  HostListener,
  inject,
  Input,
  signal,
  untracked,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormControl, FormGroupDirective, NgControl, NgForm, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';

import { TagGridWithInputService } from './tag-grid-with-input.service';
import { AutocompleteOriginDirective } from '../autocomplete/autocomplete-origin.directive';
import { FormFieldControl } from '../form-field/form-field-control';
import { ErrorStateMatcher } from '../forms/error-options';
import { ErrorStateTracker } from '../forms/error-state-tracker';
import { IconDirective } from '../icon/icon.directive';
import { InputDirective } from '../input';
import { TooltipDirective } from '../tooltip/tooltip.directive';

// TODO: We plan to unify Tag Grid Components: https://dev.azure.com/treetopmedical/Treetop.Medical/_workitems/edit/3965
/**
 * A component that allows the user to add own tags.
 * The added tags are displayed as chips.
 * The value property must have a property `id` that is unique for each option.
 */
@Component({
  selector: 'tt-tag-grid-with-input',
  standalone: true,
  imports: [ReactiveFormsModule, InputDirective, AutocompleteOriginDirective, MatIconModule, IconDirective, TooltipDirective],
  templateUrl: './tag-grid-with-input.component.html',
  styleUrls: ['tag-grid-with-input.component.scss'],
  providers: [TagGridWithInputService, { provide: FormFieldControl, useExisting: TagGridWithInputComponent }],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TagGridWithInputComponent implements FormFieldControl, DoCheck, ControlValueAccessor {
  private service = inject(TagGridWithInputService);
  public ngControl = inject(NgControl, { optional: true, self: true });
  private defaultErrorStateMatcher = inject(ErrorStateMatcher);
  private parentForm = inject(NgForm, { optional: true });
  private parentFormGroup = inject(FormGroupDirective, { optional: true });

  public _error = signal(false);
  @Input()
  public set error(value: boolean) {
    this._error.set(value);
  }
  protected _currentlyAddedOption = this.service.currentlyAddedOption;

  protected _placeholder = signal('');
  @Input()
  public set placeholder(value: string) {
    this._placeholder.set(value);
  }

  protected _disabled = signal(false);
  @Input()
  public set disabled(disabled: boolean) {
    this._disabled.set(disabled);
  }

  @HostListener('keydown.enter', ['$event'])
  public onEnter(event: KeyboardEvent) {
    // To prevent form from sending the event to the first form element
    event.preventDefault();
  }

  protected _userAddedOptions = this.service.userAddedOptions;
  public errorState = computed(() => this.errorStateTracker.errorState());
  protected searchControl = new FormControl('');

  private errorStateTracker: ErrorStateTracker;

  constructor() {
    toObservable(this._userAddedOptions).subscribe((userAddedOptions) => {
      this._onChange(userAddedOptions);
    });

    toObservable(this._disabled).subscribe((disabled) => disabled && this.searchControl.disable());

    if (this.ngControl !== null) {
      this.ngControl.valueAccessor = this;
    }

    this.errorStateTracker = new ErrorStateTracker(this.defaultErrorStateMatcher, this.ngControl, this.parentFormGroup, this.parentForm);

    effect(() => {
      if (this.ngControl) {
        const errorState = this.errorStateTracker.errorState();
        untracked(() => this._error.set(errorState));
      }
    });
  }

  protected onInputChange(value: KeyboardEvent): void {
    if (value.key === 'Enter') {
      this.service.setUserAddedOption(this.searchControl.value);
      this.service.addUserAddedOption();
      this.searchControl.setValue('');
    }

    if (value.key === 'Backspace' && this.searchControl.value === '') {
      this.service.removeSelection(this._userAddedOptions()[this._userAddedOptions().length - 1]);
    }
  }

  protected removeTag(event: MouseEvent, tag: string): void {
    event.preventDefault();
    this.service.removeSelection(tag);
  }

  // ------------- ControlValueAccessor related methods -------------

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected _onTouched = () => {};

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected _onChange: (value: unknown) => void = () => {};

  public ngDoCheck() {
    if (this.ngControl) {
      this.errorStateTracker.updateErrorState();
    }
  }

  public writeValue(value: string[]): void {
    this.service.setUserAddedOptions(value);
  }

  public registerOnChange(fn: (value: unknown) => void): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }
}
